import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AIChatBox } from '../AIChatBox';
import { PatientNameDisplay } from '../PatientComponents';
import { PatientSearchModal } from './PatientSearchModal';
import { createTask, searchPatients } from '../../firebase';
import { canSeeSitemap } from '../../utils/roleUtils';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';

// Widget Components
function StatsWidget({ label, value, icon, color, description }: any) {
  return (
    <div style={{
      padding: '16px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px'
    }}>
      <div style={{ fontSize: '32px', marginBottom: '4px' }}>{icon}</div>
      <div style={{ fontSize: '28px', fontWeight: 'bold', color: color || '#007acc', marginBottom: '2px', lineHeight: '1.2' }}>
        {value}
      </div>
      <div style={{ 
        fontSize: '13px', 
        color: '#666', 
        textAlign: 'center',
        lineHeight: '1.3',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-word',
        fontWeight: '500'
      }}>{label}</div>
      {description && (
        <div style={{ 
          fontSize: '11px', 
          color: '#999', 
          textAlign: 'center',
          lineHeight: '1.4',
          marginTop: '4px',
          padding: '0 8px'
        }}>{description}</div>
      )}
    </div>
  );
}

// Combined Patients Widget with Pie Chart
function PatientsOverviewWidget({ stats }: any) {
  const { t } = useTranslation();
  const total = stats?.totalPatients || 0;
  const active = stats?.activePatients || 0;
  const inactive = Math.max(0, total - active);
  const activePercentage = total > 0 ? Math.round((active / total) * 100) : 0;
  const inactivePercentage = total > 0 ? Math.round((inactive / total) * 100) : 0;
  
  // Simple pie chart using SVG
  const radius = 30;
  const circumference = 2 * Math.PI * radius;
  const activeArcLength = total > 0 ? (activePercentage / 100) * circumference : 0;
  const inactiveArcLength = total > 0 && inactive > 0 ? (inactivePercentage / 100) * circumference : 0;
  
  return (
    <div style={{
      padding: '12px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}>
      <div style={{ fontSize: '11px', color: '#666', fontWeight: '500', marginBottom: '6px', textAlign: 'center', lineHeight: '1.1' }}>
        {t('totalPatients')}
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', flex: 1, minHeight: 0 }}>
        {/* Pie Chart */}
        <div style={{ position: 'relative', width: '70px', height: '70px', flexShrink: 0 }}>
          <svg width="70" height="70" style={{ transform: 'rotate(-90deg)' }}>
            <circle
              cx="35"
              cy="35"
              r={radius - 5}
              fill="none"
              stroke="#e0e0e0"
              strokeWidth="7"
            />
            {active > 0 && (
              <circle
                cx="35"
                cy="35"
                r={radius - 5}
                fill="none"
                stroke="#28a745"
                strokeWidth="7"
                strokeDasharray={`${activeArcLength} ${circumference}`}
                strokeLinecap="round"
              />
            )}
            {inactive > 0 && (
              <circle
                cx="35"
                cy="35"
                r={radius - 5}
                fill="none"
                stroke="#dc3545"
                strokeWidth="7"
                strokeDasharray={`${inactiveArcLength} ${circumference}`}
                strokeDashoffset={-activeArcLength}
                strokeLinecap="round"
              />
            )}
          </svg>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#333',
            lineHeight: '1'
          }}>
            {total}
          </div>
        </div>
        
        {/* Stats - Below chart */}
        <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#28a745', flexShrink: 0 }}></div>
            <div style={{ fontSize: '10px', color: '#333', lineHeight: '1.1', whiteSpace: 'nowrap' }}>
              <strong>{active}</strong> {t('dashboard.active') || 'Active'}
            </div>
          </div>
          {inactive > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#dc3545', flexShrink: 0 }}></div>
              <div style={{ fontSize: '10px', color: '#333', lineHeight: '1.1', whiteSpace: 'nowrap' }}>
                <strong>{inactive}</strong> {t('dashboard.inactive') || 'Inactive'}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Keep old widgets for backward compatibility but mark as deprecated
function TotalPatientsWidget({ stats }: any) {
  const { t } = useTranslation();
  return <StatsWidget label={t('totalPatients')} value={stats?.totalPatients || 0} icon="👥" color="#007acc" />;
}

function ActivePatientsWidget({ stats }: any) {
  const { t } = useTranslation();
  return <StatsWidget label={t('activePatients')} value={stats?.activePatients || 0} icon="✅" color="#28a745" />;
}

// Combined Events Widget with Bar Chart
function EventsOverviewWidget({ stats }: any) {
  const { t } = useTranslation();
  const total = stats?.totalEvents || 0;
  const upcoming = stats?.upcomingEvents || 0;
  const past = total - upcoming;
  const maxValue = Math.max(total, 1);
  const upcomingPercentage = (upcoming / maxValue) * 100;
  const pastPercentage = (past / maxValue) * 100;
  
  // Calculate bar heights for visual graph (max height 80px for 2-column widget)
  const maxBarHeight = 80;
  const upcomingBarHeight = total > 0 ? (upcoming / maxValue) * maxBarHeight : 0;
  const pastBarHeight = total > 0 ? (past / maxValue) * maxBarHeight : 0;
  
  return (
    <div style={{
      padding: '12px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}>
      <div style={{ fontSize: '11px', color: '#666', fontWeight: '500', marginBottom: '6px', textAlign: 'center', lineHeight: '1.1' }}>
        {t('totalEvents')}
      </div>
      
      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff6b6b', textAlign: 'center', marginBottom: '8px', lineHeight: '1' }}>
        {total}
      </div>
      
      {/* Bar Chart Graph */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        alignItems: 'flex-start', 
        justifyContent: 'center', 
        gap: '12px', 
        flex: 1, 
        minHeight: 0,
        padding: '8px 0'
      }}>
        {/* Upcoming Events Bar */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
          <div style={{ textAlign: 'center', width: '100%', marginBottom: '4px' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#ffa500', lineHeight: '1.2' }}>
              {upcoming}
            </div>
            <div style={{ fontSize: '10px', color: '#666', marginTop: '2px', lineHeight: '1.1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t('dashboard.upcoming') || 'Upcoming'}
            </div>
          </div>
            <div style={{
              width: '100%',
              maxWidth: '50px',
              height: `${maxBarHeight}px`,
              backgroundColor: '#f0f0f0',
              borderRadius: '6px 6px 0 0',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'flex-end',
              position: 'relative'
            }}>
            <div style={{
              width: '100%',
              height: `${upcomingBarHeight}px`,
              backgroundColor: '#ffa500',
              transition: 'height 0.8s ease-out, opacity 0.8s ease-out',
              borderRadius: upcomingBarHeight === maxBarHeight ? '6px 6px 0 0' : '0',
              minHeight: upcoming > 0 ? '4px' : '0',
              opacity: 1
            }}></div>
          </div>
        </div>
        
        {/* Past Events Bar */}
        {past > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
            <div style={{ textAlign: 'center', width: '100%', marginBottom: '4px' }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#6c757d', lineHeight: '1.2' }}>
                {past}
              </div>
              <div style={{ fontSize: '10px', color: '#666', marginTop: '2px', lineHeight: '1.1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t('dashboard.past') || 'Past'}
              </div>
            </div>
            <div style={{
              width: '100%',
              maxWidth: '50px',
              height: `${maxBarHeight}px`,
              backgroundColor: '#f0f0f0',
              borderRadius: '6px 6px 0 0',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'flex-end',
              position: 'relative'
            }}>
              <div style={{
                width: '100%',
                height: `${pastBarHeight}px`,
                backgroundColor: '#6c757d',
                transition: 'height 0.8s ease-out, opacity 0.8s ease-out',
                borderRadius: pastBarHeight === maxBarHeight ? '6px 6px 0 0' : '0',
                minHeight: past > 0 ? '4px' : '0',
                opacity: 1
              }}></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Keep old widgets for backward compatibility
function TotalEventsWidget({ stats }: any) {
  const { t } = useTranslation();
  return <StatsWidget label={t('totalEvents')} value={stats?.totalEvents || 0} icon="📅" color="#ff6b6b" />;
}

function UpcomingEventsWidget({ stats }: any) {
  const { t } = useTranslation();
  return <StatsWidget label={t('upcomingEvents')} value={stats?.upcomingEvents || 0} icon="⏰" color="#ffa500" />;
}

// Combined Tasks Widget with Progress Bar
function TasksOverviewWidget({ stats }: any) {
  const { t } = useTranslation();
  const total = stats?.totalTasks || 0;
  const pending = stats?.pendingTasks || 0;
  const completed = total - pending;
  const completionPercentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  
  return (
    <div style={{
      padding: '12px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}>
      <div style={{ fontSize: '11px', color: '#666', fontWeight: '500', marginBottom: '6px', textAlign: 'center', lineHeight: '1.1' }}>
        {t('totalTasks')}
      </div>
      
      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6c757d', textAlign: 'center', marginBottom: '8px', lineHeight: '1' }}>
        {total}
      </div>
      
      {/* Progress Bar */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, justifyContent: 'center', minHeight: 0 }}>
        <div style={{
          width: '100%',
          height: '20px',
          backgroundColor: '#f0f0f0',
          borderRadius: '10px',
          overflow: 'hidden',
          position: 'relative',
          marginBottom: '6px'
        }}>
          <div style={{
            width: `${completionPercentage}%`,
            height: '100%',
            backgroundColor: '#28a745',
            transition: 'width 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '10px',
            fontWeight: '600',
            lineHeight: '1'
          }}>
            {completionPercentage > 18 && `${completionPercentage}%`}
          </div>
          {completionPercentage <= 18 && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '10px',
              fontWeight: '600',
              color: '#666',
              lineHeight: '1'
            }}>
              {completionPercentage}%
            </div>
          )}
        </div>
      </div>
      
      {/* Chart details below progress bar */}
      <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#28a745', flexShrink: 0 }}></div>
          <span style={{ fontSize: '10px', color: '#333', lineHeight: '1.1', whiteSpace: 'nowrap' }}><strong>{completed}</strong> {t('dashboard.done') || 'Done'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#dc3545', flexShrink: 0 }}></div>
          <span style={{ fontSize: '10px', color: '#333', lineHeight: '1.1', whiteSpace: 'nowrap' }}><strong>{pending}</strong> {t('dashboard.pending') || 'Pending'}</span>
        </div>
      </div>
    </div>
  );
}

// Keep old widgets for backward compatibility
function TotalTasksWidget({ stats }: any) {
  const { t } = useTranslation();
  return <StatsWidget label={t('totalTasks')} value={stats?.totalTasks || 0} icon="📋" color="#6c757d" />;
}

function PendingTasksWidget({ stats }: any) {
  const { t } = useTranslation();
  return <StatsWidget 
    label={t('pendingTasks')} 
    value={stats?.pendingTasks || 0} 
    icon="⏳" 
    color="#dc3545"
  />;
}

function CompletionRateWidget({ stats, user }: any) {
  const { t } = useTranslation();
  
  // Only show to program managers, department managers, and super admins
  if (!canSeeSitemap(user)) {
    return null;
  }
  
  // Display completion percentage (archived cases / total cases)
  const percentage = stats?.treatmentCompletionPercentage || 0;
  const incompletePercentage = 100 - percentage;
  
  // Pie chart using SVG
  const radius = 30;
  const circumference = 2 * Math.PI * radius;
  const completedArcLength = (percentage / 100) * circumference;
  const incompleteArcLength = (incompletePercentage / 100) * circumference;
  
  return (
    <div style={{
      padding: '12px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}>
      <div style={{ fontSize: '11px', color: '#666', fontWeight: '500', marginBottom: '6px', textAlign: 'center', lineHeight: '1.1' }}>
        {t('completionRate')}
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', flex: 1, minHeight: 0 }}>
        {/* Pie Chart */}
        <div style={{ position: 'relative', width: '70px', height: '70px', flexShrink: 0 }}>
          <svg width="70" height="70" style={{ transform: 'rotate(-90deg)' }}>
            <circle
              cx="35"
              cy="35"
              r={radius}
              fill="none"
              stroke="#e0e0e0"
              strokeWidth="7"
            />
            {percentage > 0 && (
              <circle
                cx="35"
                cy="35"
                r={radius}
                fill="none"
                stroke="#17a2b8"
                strokeWidth="7"
                strokeDasharray={`${completedArcLength} ${circumference}`}
                strokeLinecap="round"
              />
            )}
            {incompletePercentage > 0 && percentage > 0 && (
              <circle
                cx="35"
                cy="35"
                r={radius}
                fill="none"
                stroke="#dc3545"
                strokeWidth="7"
                strokeDasharray={`${incompleteArcLength} ${circumference}`}
                strokeDashoffset={-completedArcLength}
                strokeLinecap="round"
              />
            )}
          </svg>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#333',
            lineHeight: '1'
          }}>
            {percentage}%
          </div>
        </div>
        
        {/* Stats - Below chart */}
        <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#17a2b8', flexShrink: 0 }}></div>
            <div style={{ fontSize: '10px', color: '#333', lineHeight: '1.1', whiteSpace: 'nowrap' }}>
              <strong>{percentage}%</strong> {t('dashboard.done') || 'Done'}
            </div>
          </div>
          {incompletePercentage > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#dc3545', flexShrink: 0 }}></div>
              <div style={{ fontSize: '10px', color: '#333', lineHeight: '1.1', whiteSpace: 'nowrap' }}>
                <strong>{incompletePercentage}%</strong> {t('dashboard.left') || 'Left'}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NewPatientsWidget({ stats }: any) {
  const { t } = useTranslation();
  return <StatsWidget label={t('newPatientsThisMonth')} value={stats?.newPatientsThisMonth || 0} icon="✨" color="#9c27b0" />;
}

function RecentActivityWidget({ activityLogs, handlePatientSelect, handleDeleteActivityLog, showConfirm }: any) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  
  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return '';
    try {
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return new Date(timestamp.toDate()).toLocaleString();
      }
      return new Date(timestamp).toLocaleString();
    } catch (e) {
      return '';
    }
  };
  
  const handleClick = (log: any) => {
    if (log.caseId && handlePatientSelect) {
      handlePatientSelect(log.caseId, log.id);
    }
  };
  
  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
      <h3 style={{ 
        margin: '0 0 12px 0', 
        fontSize: '16px', 
        fontWeight: '600',
        color: '#333',
        textAlign: isRTL ? 'right' : 'left'
      }}>
        {t('recentActivity')}
      </h3>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {activityLogs && activityLogs.length > 0 ? (
          activityLogs.slice(0, 5).map((log: any, idx: number) => (
            <div
              key={idx}
              style={{
                padding: '10px',
                marginBottom: '8px',
                background: '#f8f9fa',
                borderRadius: '6px',
                borderLeft: isRTL ? 'none' : '3px solid #007acc',
                borderRight: isRTL ? '3px solid #007acc' : 'none',
                fontSize: '13px',
                cursor: log.caseId ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                overflow: 'hidden',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (log.caseId) {
                  e.currentTarget.style.transform = 'translateX(2px)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (log.caseId) {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <div 
                onClick={() => handleClick(log)}
                style={{ paddingRight: '24px' }}
              >
                <div style={{ fontWeight: '500', color: '#333', marginBottom: '4px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                  {log.action}
                </div>
              <div style={{ fontSize: '11px', color: '#666', display: 'flex', gap: '8px', flexWrap: 'wrap', flexDirection: 'column', overflow: 'hidden' }}>
                {log.userName && (
                  <span style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    👤 {log.userName}
                  </span>
                )}
                {log.patientName && (
                  <span style={{ fontWeight: '500', color: '#333', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    🏥 {log.patientName}
                  </span>
                )}
                {log.caseId && (
                  <span style={{ color: '#007acc', fontWeight: '500', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    {log.caseId}
                  </span>
                )}
                {log.timestamp && (
                  <span style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    🕐 {formatTimestamp(log.timestamp)}
                  </span>
                )}
              </div>
              </div>
              {handleDeleteActivityLog && log.id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (showConfirm) {
                      showConfirm(
                        t('dashboard.confirmDeleteActivity') || 'Are you sure you want to delete this activity log?',
                        async () => {
                          try {
                            await handleDeleteActivityLog(log.id);
                          } catch (error) {
                            console.error('Error deleting activity log:', error);
                          }
                        }
                      );
                    } else {
                      handleDeleteActivityLog(log.id);
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    [isRTL ? 'left' : 'right']: '8px',
                    background: 'rgba(220, 53, 69, 0.1)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    color: '#dc3545',
                    fontWeight: 'bold',
                    transition: 'all 0.2s ease',
                    padding: 0,
                    lineHeight: 1
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dc3545';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(220, 53, 69, 0.1)';
                    e.currentTarget.style.color = '#dc3545';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={t('dashboard.deleteActivity') || 'Delete activity'}
                >
                  ×
                </button>
              )}
            </div>
          ))
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px', 
            color: '#999' 
          }}>
            {t('noRecentActivity')}
          </div>
        )}
      </div>
    </div>
  );
}

function TodayTasksWidget({ todayTasks, handlePatientSelect, user, refreshTasks, handleDeleteTask, showConfirm }: any) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    dueDate: new Date().toISOString().split('T')[0],
    caseId: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [caseSearchTerm, setCaseSearchTerm] = useState('');
  const [caseSearchResults, setCaseSearchResults] = useState<any[]>([]);
  const [isSearchingCases, setIsSearchingCases] = useState(false);
  const [showCaseDropdown, setShowCaseDropdown] = useState(false);
  const [selectedCase, setSelectedCase] = useState<any>(null);

  // Handle case search
  const handleCaseSearch = async (searchTerm: string) => {
    setCaseSearchTerm(searchTerm);
    
    if (searchTerm.length < 2) {
      setCaseSearchResults([]);
      setShowCaseDropdown(false);
      return;
    }

    setIsSearchingCases(true);
    setShowCaseDropdown(true);
    try {
      const results = await searchPatients(searchTerm);
      setCaseSearchResults(results);
    } catch (error) {
      console.error('Error searching cases:', error);
      setCaseSearchResults([]);
    } finally {
      setIsSearchingCases(false);
    }
  };

  // Handle case selection
  const handleSelectCase = (patient: any) => {
    setSelectedCase(patient);
    setTaskForm({ ...taskForm, caseId: patient.caseId || patient.id });
    setCaseSearchTerm(patient.caseId || patient.id);
    setShowCaseDropdown(false);
    setCaseSearchResults([]);
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!showTaskDialog) {
      setCaseSearchTerm('');
      setCaseSearchResults([]);
      setSelectedCase(null);
      setShowCaseDropdown(false);
    }
  }, [showTaskDialog]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showCaseDropdown && !target.closest('[data-case-search]')) {
        setShowCaseDropdown(false);
      }
    };

    if (showCaseDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCaseDropdown]);
  
  const formatDate = (date: any) => {
    if (!date) return '';
    try {
      if (date.toDate && typeof date.toDate === 'function') {
        return new Date(date.toDate()).toLocaleDateString();
      }
      return new Date(date).toLocaleDateString();
    } catch (e) {
      return '';
    }
  };
  
  const handleClick = (task: any) => {
    if (task.caseId && handlePatientSelect) {
      handlePatientSelect(task.caseId);
    }
  };

  const handleAddTask = async () => {
    if (!taskForm.title.trim()) {
      return;
    }

    try {
      setIsSaving(true);
      // Always use today's date for tasks created from Today's Tasks widget (local timezone)
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayDate = `${year}-${month}-${day}`;
      console.log('Creating task with dueDate:', todayDate, 'for user:', user?.email || user?.id);
      // Patient name will be fetched automatically when task is displayed if caseId is provided
      const result = await createTask({
        title: taskForm.title,
        description: taskForm.description,
        priority: taskForm.priority,
        dueDate: todayDate,
        caseId: taskForm.caseId || undefined,
        patientName: undefined // Will be populated when task is displayed based on caseId
      }, user?.email || user?.id || 'unknown');

      if (result.success) {
        setShowTaskDialog(false);
        setTaskForm({
          title: '',
          description: '',
          priority: 'medium',
          dueDate: new Date().toISOString().split('T')[0],
          caseId: ''
        });
        // Wait a bit for Firebase to save, then refresh
        if (refreshTasks) {
          setTimeout(() => {
            refreshTasks();
          }, 500);
        }
      }
    } catch (error) {
      console.error('Error creating task:', error);
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <h3 style={{ 
        margin: '0 0 12px 0', 
        fontSize: '16px', 
        fontWeight: '600',
        color: '#333',
        textAlign: isRTL ? 'right' : 'left'
      }}>
        {t('dashboard.todayTasks')} ({todayTasks?.length || 0})
      </h3>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {todayTasks && todayTasks.length > 0 ? (
          todayTasks.slice(0, 5).map((task: any, idx: number) => (
            <div
              key={idx}
              style={{
                padding: '10px',
                marginBottom: '8px',
                background: task.status === 'completed' ? '#e8f5e9' : '#fff3e0',
                borderRadius: '6px',
                borderLeft: isRTL ? 'none' : `3px solid ${task.status === 'completed' ? '#4caf50' : '#ff9800'}`,
                borderRight: isRTL ? `3px solid ${task.status === 'completed' ? '#4caf50' : '#ff9800'}` : 'none',
                fontSize: '13px',
                cursor: task.caseId ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (task.caseId) {
                  e.currentTarget.style.transform = 'translateX(2px)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (task.caseId) {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <div 
                onClick={() => handleClick(task)}
                style={{ paddingRight: '24px' }}
              >
                <div style={{ fontWeight: '500', color: '#333', marginBottom: '4px' }}>
                  {task.title}
                </div>
              <div style={{ fontSize: '11px', color: '#666', display: 'flex', gap: '8px', flexWrap: 'wrap', flexDirection: 'column' }}>
                {task.patientName && (
                  <span style={{ fontWeight: '500', color: '#333' }}>
                    👤 {task.patientName}
                  </span>
                )}
                {task.caseId && (
                  <span style={{ color: '#007acc', fontWeight: '500' }}>
                    {task.caseId}
                  </span>
                )}
                {task.dueDate && (
                  <span>⏰ {formatDate(task.dueDate)}</span>
                )}
              </div>
              </div>
              {handleDeleteTask && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (showConfirm) {
                      showConfirm(
                        t('dashboard.confirmDeleteTask') || 'Are you sure you want to delete this task?',
                        async () => {
                          try {
                            await handleDeleteTask(task.id);
                            if (refreshTasks) refreshTasks();
                          } catch (error) {
                            console.error('Error deleting task:', error);
                          }
                        }
                      );
                    } else {
                      handleDeleteTask(task.id);
                      if (refreshTasks) refreshTasks();
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    [isRTL ? 'left' : 'right']: '8px',
                    background: 'rgba(220, 53, 69, 0.1)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    color: '#dc3545',
                    fontWeight: 'bold',
                    transition: 'all 0.2s ease',
                    padding: 0,
                    lineHeight: 1
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dc3545';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(220, 53, 69, 0.1)';
                    e.currentTarget.style.color = '#dc3545';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={t('dashboard.deleteTask') || 'Delete task'}
                >
                  ×
                </button>
              )}
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
            {t('dashboard.noTasksToday')}
          </div>
        )}
      </div>
      <button
        onClick={() => setShowTaskDialog(true)}
        style={{
          position: 'absolute',
          bottom: '16px',
          right: '16px',
          width: '36px',
          height: '36px',
          backgroundColor: '#007acc',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          cursor: 'pointer',
          fontSize: '20px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0, 122, 204, 0.3)',
          transition: 'all 0.3s ease',
          zIndex: 10
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#005a9e';
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 122, 204, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#007acc';
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 122, 204, 0.3)';
        }}
      >
        +
      </button>

      {/* Task Creation Dialog - Rendered via Portal */}
      {showTaskDialog && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }} onClick={() => setShowTaskDialog(false)}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            direction: isRTL ? 'rtl' : 'ltr'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '20px 30px 15px 30px',
              borderBottom: '1px solid #e9ecef'
            }}>
              <h3 style={{ margin: 0, color: '#000000' }}>
                {t('dashboard.addTask') || 'Add Task'}
              </h3>
              <button
                onClick={() => setShowTaskDialog(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ padding: '30px', overflowY: 'auto' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                  {t('dashboard.taskTitle') || 'Task Title'} *
                </label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder={t('dashboard.enterTaskTitle') || 'Enter task title'}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                  {t('dashboard.taskDescription') || 'Description'}
                </label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                  placeholder={t('dashboard.enterTaskDescription') || 'Enter task description'}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                  {t('dashboard.priority') || 'Priority'}
                </label>
                <select
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value as any })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="low">{t('dashboard.priorityLow') || 'Low'}</option>
                  <option value="medium">{t('dashboard.priorityMedium') || 'Medium'}</option>
                  <option value="high">{t('dashboard.priorityHigh') || 'High'}</option>
                  <option value="urgent">{t('dashboard.priorityUrgent') || 'Urgent'}</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px', position: 'relative' }} data-case-search>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                  {t('dashboard.caseId') || 'Case ID (Optional)'}
                </label>
                <input
                  type="text"
                  value={caseSearchTerm}
                  onChange={(e) => handleCaseSearch(e.target.value)}
                  onFocus={() => {
                    if (caseSearchResults.length > 0) {
                      setShowCaseDropdown(true);
                    }
                  }}
                  placeholder={t('dashboard.searchCase') || 'Search for case ID...'}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                {isSearchingCases && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    padding: '8px',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    marginTop: '4px',
                    fontSize: '14px',
                    color: '#666',
                    zIndex: 1000
                  }}>
                    {t('common.loading') || 'Loading...'}
                  </div>
                )}
                {showCaseDropdown && caseSearchResults.length > 0 && !isSearchingCases && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    marginTop: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    zIndex: 1000
                  }}>
                    {caseSearchResults.map((patient) => (
                      <div
                        key={patient.caseId || patient.id}
                        onClick={() => handleSelectCase(patient)}
                        style={{
                          padding: '10px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          transition: 'background 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f5f5f5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                        }}
                      >
                        {(patient.firstName || patient.lastName) ? (
                          <>
                            <div style={{ fontWeight: '500', color: '#333' }}>
                              {patient.firstName} {patient.lastName}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                              {patient.caseId || patient.id}
                            </div>
                          </>
                        ) : (
                          <div style={{ fontWeight: '500', color: '#333' }}>
                            {patient.caseId || patient.id}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{ 
              padding: '20px 30px', 
              borderTop: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowTaskDialog(false)}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleAddTask}
                disabled={!taskForm.title.trim() || isSaving}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: !taskForm.title.trim() || isSaving ? '#ccc' : '#007acc',
                  color: 'white',
                  cursor: !taskForm.title.trim() || isSaving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background 0.3s ease'
                }}
              >
                {isSaving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function PendingTasksListWidget({ pendingTasks, handlePatientSelect, handleDeleteTask, showConfirm, refreshTasks }: any) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  
  const formatDate = (date: any) => {
    if (!date) return '';
    try {
      if (date.toDate && typeof date.toDate === 'function') {
        return new Date(date.toDate()).toLocaleDateString();
      }
      return new Date(date).toLocaleDateString();
    } catch (e) {
      return '';
    }
  };
  
  const handleClick = (task: any) => {
    if (task.caseId && handlePatientSelect) {
      handlePatientSelect(task.caseId);
    }
  };
  
  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <h3 style={{ 
        margin: '0 0 12px 0', 
        fontSize: '16px', 
        fontWeight: '600',
        color: '#333',
        textAlign: isRTL ? 'right' : 'left'
      }}>
        {t('dashboard.pendingTasksList')} ({pendingTasks?.length || 0})
      </h3>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {pendingTasks && pendingTasks.length > 0 ? (
          pendingTasks.slice(0, 5).map((task: any, idx: number) => (
            <div
              key={idx}
              style={{
                padding: '10px',
                marginBottom: '8px',
                background: '#ffebee',
                borderRadius: '6px',
                borderLeft: isRTL ? 'none' : '3px solid #f44336',
                borderRight: isRTL ? '3px solid #f44336' : 'none',
                fontSize: '13px',
                cursor: task.caseId ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (task.caseId) {
                  e.currentTarget.style.transform = 'translateX(2px)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (task.caseId) {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <div 
                onClick={() => handleClick(task)}
                style={{ paddingRight: '24px' }}
              >
                <div style={{ fontWeight: '500', color: '#333', marginBottom: '4px' }}>
                  {task.title}
                </div>
              <div style={{ fontSize: '11px', color: '#666', display: 'flex', gap: '8px', flexWrap: 'wrap', flexDirection: 'column' }}>
                {task.patientName && (
                  <span style={{ fontWeight: '500', color: '#333' }}>
                    👤 {task.patientName}
                  </span>
                )}
                {task.caseId && (
                  <span style={{ color: '#007acc', fontWeight: '500' }}>
                    {task.caseId}
                  </span>
                )}
                {task.dueDate && (
                  <span style={{ color: '#d32f2f' }}>
                    ⚠️ {formatDate(task.dueDate)}
                  </span>
                )}
              </div>
              </div>
              {handleDeleteTask && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (showConfirm) {
                      showConfirm(
                        t('dashboard.confirmDeleteTask') || 'Are you sure you want to delete this task?',
                        async () => {
                          try {
                            await handleDeleteTask(task.id);
                            if (refreshTasks) refreshTasks();
                          } catch (error) {
                            console.error('Error deleting task:', error);
                          }
                        }
                      );
                    } else {
                      handleDeleteTask(task.id);
                      if (refreshTasks) refreshTasks();
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    [isRTL ? 'left' : 'right']: '8px',
                    background: 'rgba(220, 53, 69, 0.1)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    color: '#dc3545',
                    fontWeight: 'bold',
                    transition: 'all 0.2s ease',
                    padding: 0,
                    lineHeight: 1
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dc3545';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(220, 53, 69, 0.1)';
                    e.currentTarget.style.color = '#dc3545';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={t('dashboard.deleteTask') || 'Delete task'}
                >
                  ×
                </button>
              )}
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
            {t('dashboard.noPendingTasks')}
          </div>
        )}
      </div>
    </div>
  );
}

function NotificationsWidget({ notifications, handleDeleteNotification, showConfirm }: any) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const unreadCount = notifications?.filter((n: any) => !n.read).length || 0;
  
  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <h3 style={{ 
        margin: '0 0 12px 0', 
        fontSize: '16px', 
        fontWeight: '600',
        color: '#333',
        textAlign: isRTL ? 'right' : 'left',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        {t('dashboard.notifications')}
        {unreadCount > 0 && (
          <span style={{
            background: '#f44336',
            color: 'white',
            borderRadius: '12px',
            padding: '2px 8px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            {unreadCount}
          </span>
        )}
      </h3>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {notifications && notifications.length > 0 ? (
          notifications.slice(0, 5).map((notif: any, idx: number) => (
            <div
              key={idx}
              style={{
                padding: '10px',
                marginBottom: '8px',
                background: notif.read ? '#f8f9fa' : '#e3f2fd',
                borderRadius: '6px',
                borderLeft: isRTL ? 'none' : `3px solid ${notif.read ? '#ccc' : '#2196f3'}`,
                borderRight: isRTL ? `3px solid ${notif.read ? '#ccc' : '#2196f3'}` : 'none',
                fontSize: '13px',
                opacity: notif.read ? 0.6 : 1,
                position: 'relative'
              }}
            >
              <div style={{ paddingRight: '24px' }}>
                <div style={{ fontWeight: '500', color: '#333', marginBottom: '4px' }}>
                  {notif.title}
                </div>
                <div style={{ fontSize: '11px', color: '#666' }}>
                  {notif.message}
                </div>
              </div>
              {handleDeleteNotification && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (showConfirm) {
                      showConfirm(
                        t('dashboard.confirmDeleteNotification') || 'Are you sure you want to delete this notification?',
                        async () => {
                          try {
                            await handleDeleteNotification(notif.id);
                          } catch (error) {
                            console.error('Error deleting notification:', error);
                          }
                        }
                      );
                    } else {
                      handleDeleteNotification(notif.id);
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    [isRTL ? 'left' : 'right']: '8px',
                    background: 'rgba(220, 53, 69, 0.1)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    color: '#dc3545',
                    fontWeight: 'bold',
                    transition: 'all 0.2s ease',
                    padding: 0,
                    lineHeight: 1
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dc3545';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(220, 53, 69, 0.1)';
                    e.currentTarget.style.color = '#dc3545';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={t('dashboard.deleteNotification') || 'Delete notification'}
                >
                  ×
                </button>
              )}
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
            {t('dashboard.noNotifications')}
          </div>
        )}
      </div>
    </div>
  );
}

function TodayMilestonesWidget({ todayMilestones, handlePatientSelect, handleDeleteMilestone, showConfirm }: any) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [showPatientSearch, setShowPatientSearch] = useState(false);
  
  const handleClick = (milestone: any) => {
    if (milestone.caseId && handlePatientSelect) {
      handlePatientSelect(milestone.caseId);
    }
  };

  const handleTitleClick = () => {
    setShowPatientSearch(true);
  };

  const handlePatientSelectFromSearch = (patient: any) => {
    if (patient.caseId && handlePatientSelect) {
      handlePatientSelect(patient.caseId);
    }
    setShowPatientSearch(false);
  };
  
  return (
    <>
      <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <h3 
          onClick={handleTitleClick}
          style={{ 
            margin: '0 0 12px 0', 
            fontSize: '16px', 
            fontWeight: '600',
            color: '#333',
            textAlign: isRTL ? 'right' : 'left',
            cursor: 'pointer',
            transition: 'color 0.2s ease',
            userSelect: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#007acc';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#333';
          }}
        >
          {t('dashboard.todayMilestones')} ({todayMilestones?.length || 0})
        </h3>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {todayMilestones && todayMilestones.length > 0 ? (
          todayMilestones.slice(0, 5).map((milestone: any, idx: number) => (
            <div
              key={idx}
              style={{
                padding: '10px',
                marginBottom: '8px',
                background: milestone.status === 'completed' ? '#e8f5e9' : '#fff9e6',
                borderRadius: '6px',
                borderLeft: isRTL ? 'none' : `3px solid ${milestone.status === 'completed' ? '#4caf50' : '#ffc107'}`,
                borderRight: isRTL ? `3px solid ${milestone.status === 'completed' ? '#4caf50' : '#ffc107'}` : 'none',
                fontSize: '13px',
                cursor: milestone.caseId ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (milestone.caseId) {
                  e.currentTarget.style.transform = 'translateX(2px)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (milestone.caseId) {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <div 
                onClick={() => handleClick(milestone)}
                style={{ paddingRight: '24px' }}
              >
                <div style={{ fontWeight: '500', color: '#333', marginBottom: '4px' }}>
                  {milestone.title}
                </div>
              <div style={{ fontSize: '11px', color: '#666', display: 'flex', gap: '8px', flexWrap: 'wrap', flexDirection: 'column' }}>
                {milestone.patientName && (
                  <span style={{ fontWeight: '500', color: '#333' }}>
                    👤 {milestone.patientName}
                  </span>
                )}
                {milestone.caseId && (
                  <span style={{ color: '#007acc', fontWeight: '500' }}>
                    {milestone.caseId}
                  </span>
                )}
              </div>
              </div>
              {handleDeleteMilestone && milestone.id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (showConfirm) {
                      showConfirm(
                        t('patientDetail.confirmDeleteMilestone') || 'Are you sure you want to delete this milestone?',
                        async () => {
                          try {
                            await handleDeleteMilestone(milestone.id);
                          } catch (error) {
                            console.error('Error deleting milestone:', error);
                          }
                        }
                      );
                    } else {
                      handleDeleteMilestone(milestone.id);
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    [isRTL ? 'left' : 'right']: '8px',
                    background: 'rgba(220, 53, 69, 0.1)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    color: '#dc3545',
                    fontWeight: 'bold',
                    transition: 'all 0.2s ease',
                    padding: 0,
                    lineHeight: 1
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dc3545';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(220, 53, 69, 0.1)';
                    e.currentTarget.style.color = '#dc3545';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={t('dashboard.deleteMilestone') || 'Delete milestone'}
                >
                  ×
                </button>
              )}
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
            {t('dashboard.noMilestonesToday')}
          </div>
        )}
      </div>
      <button
        onClick={() => setShowPatientSearch(true)}
        style={{
          position: 'absolute',
          bottom: '16px',
          [isRTL ? 'left' : 'right']: '16px',
          width: '36px',
          height: '36px',
          backgroundColor: '#007acc',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          cursor: 'pointer',
          fontSize: '20px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0, 122, 204, 0.3)',
          transition: 'all 0.3s ease',
          zIndex: 10
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#005a9e';
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 122, 204, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#007acc';
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 122, 204, 0.3)';
        }}
        title={t('dashboard.todayMilestones')}
      >
        +
      </button>
      </div>
      {showPatientSearch && (
        <PatientSearchModal
          onClose={() => setShowPatientSearch(false)}
          onSelect={handlePatientSelectFromSearch}
        />
      )}
    </>
  );
}

function InactivePatientsWidget({ inactivePatients, handlePatientSelect, handleDeletePatient, showConfirm }: any) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  
  const handleClick = (patient: any) => {
    if (patient.caseId && handlePatientSelect) {
      handlePatientSelect(patient.caseId);
    }
  };
  
  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <h3 style={{ 
        margin: '0 0 12px 0', 
        fontSize: '16px', 
        fontWeight: '600',
        color: '#333',
        textAlign: isRTL ? 'right' : 'left'
      }}>
        {t('dashboard.inactivePatientsList')} ({inactivePatients?.length || 0})
      </h3>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {inactivePatients && inactivePatients.length > 0 ? (
          inactivePatients.slice(0, 5).map((patient: any, idx: number) => (
            <div
              key={idx}
              style={{
                padding: '10px',
                marginBottom: '8px',
                background: '#fce4ec',
                borderRadius: '6px',
                borderLeft: isRTL ? 'none' : '3px solid #e91e63',
                borderRight: isRTL ? '3px solid #e91e63' : 'none',
                fontSize: '13px',
                cursor: patient.caseId ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (patient.caseId) {
                  e.currentTarget.style.transform = 'translateX(2px)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (patient.caseId) {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <div 
                onClick={() => handleClick(patient)}
                style={{ paddingRight: '24px' }}
              >
                <div style={{ fontWeight: '500', color: '#333', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>👤</span>
                  {patient.caseId ? (
                    <PatientNameDisplay caseId={patient.caseId} />
                  ) : (
                    <span>{t('dashboard.unknownPatient')}</span>
                  )}
                </div>
                <div style={{ fontSize: '11px', color: '#666', display: 'flex', gap: '8px', flexWrap: 'wrap', flexDirection: 'column' }}>
                  {patient.caseId && (
                    <span style={{ color: '#007acc', fontWeight: '500' }}>
                      {patient.caseId}
                    </span>
                  )}
                </div>
              </div>
              {handleDeletePatient && patient.caseId && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (showConfirm) {
                      showConfirm(
                        t('patients.confirmDelete') || 'Are you sure you want to delete this patient? This action cannot be undone.',
                        async () => {
                          try {
                            await handleDeletePatient(patient.caseId);
                          } catch (error) {
                            console.error('Error deleting patient:', error);
                          }
                        }
                      );
                    } else {
                      handleDeletePatient(patient.caseId);
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    [isRTL ? 'left' : 'right']: '8px',
                    background: 'rgba(220, 53, 69, 0.1)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    color: '#dc3545',
                    fontWeight: 'bold',
                    transition: 'all 0.2s ease',
                    padding: 0,
                    lineHeight: 1
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dc3545';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(220, 53, 69, 0.1)';
                    e.currentTarget.style.color = '#dc3545';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={t('patients.deletePatient') || 'Delete patient'}
                >
                  ×
                </button>
              )}
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
            {t('dashboard.noInactivePatients')}
          </div>
        )}
      </div>
    </div>
  );
}

function QuickStatsWidget({ stats }: any) {
  const { t } = useTranslation();
  
  const quickStats = [
    { label: t('totalPatients'), value: stats?.totalPatients || 0, icon: '👥', color: '#007acc' },
    { label: t('activePatients'), value: stats?.activePatients || 0, icon: '✅', color: '#28a745' },
    { label: t('upcomingEvents'), value: stats?.upcomingEvents || 0, icon: '📅', color: '#ffa500' },
    { label: t('pendingTasks'), value: stats?.pendingTasks || 0, icon: '📋', color: '#dc3545' }
  ];
  
  return (
    <div style={{ 
      padding: '12px',
      height: '100%',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '8px'
    }}>
      {quickStats.map((stat, idx) => (
        <div
          key={idx}
          style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <span style={{ fontSize: '24px', marginBottom: '6px' }}>{stat.icon}</span>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: stat.color, marginBottom: '2px' }}>
            {stat.value}
          </div>
          <div style={{ fontSize: '11px', color: '#666', textAlign: 'center' }}>{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

function InactiveCasesWidget({ stats }: any) {
  const { t } = useTranslation();
  return <StatsWidget label={t('dashboard.inactiveCases')} value={stats?.inactiveCases || 0} icon="📁" color="#dc3545" />;
}

function TreatmentCompletionWidget({ stats }: any) {
  const { t } = useTranslation();
  // Display archived cases count
  const archivedCount = stats?.treatmentCompletionCount || 0;
  return <StatsWidget label={t('dashboard.treatmentCompletion')} value={archivedCount} icon="📊" color="#28a745" />;
}

function RecentlyUpdatedWidget({ stats }: any) {
  const { t } = useTranslation();
  return <StatsWidget label={t('dashboard.recentlyUpdatedPatients')} value={stats?.recentlyUpdated || 0} icon="🔄" color="#007acc" />;
}

function TotalCasesWidget({ stats }: any) {
  const { t } = useTranslation();
  return <StatsWidget label={t('dashboard.totalCases')} value={stats?.totalCases || 0} icon="📋" color="#6c757d" />;
}

// Geographic Sitemap Widget
function GeographicSitemapWidget({ user }: any) {
  const { t, i18n } = useTranslation();
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [expandedPrograms, setExpandedPrograms] = useState<Set<string>>(new Set());
  const isRTL = i18n.language === 'he';
  const hasPermission = canSeeSitemap(user);

  // Load organizations
  useEffect(() => {
    if (!hasPermission) return;
    
    const q = query(collection(db, 'organizations'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const orgsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrganizations(orgsData);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [hasPermission]);

  // Load programs
  useEffect(() => {
    if (!hasPermission) return;
    
    const q = query(collection(db, 'programs'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const programsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPrograms(programsData);
    });
    return () => unsubscribe();
  }, [hasPermission]);

  // Load groups
  useEffect(() => {
    if (!hasPermission) return;
    
    const q = query(collection(db, 'groups'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const groupsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGroups(groupsData);
    });
    return () => unsubscribe();
  }, [hasPermission]);

  // Check permission after hooks
  if (!hasPermission) {
    return null;
  }

  const toggleOrganization = (orgId: string) => {
    const newExpanded = new Set(expandedOrgs);
    if (newExpanded.has(orgId)) {
      newExpanded.delete(orgId);
    } else {
      newExpanded.add(orgId);
    }
    setExpandedOrgs(newExpanded);
  };

  const toggleProgram = (programId: string) => {
    const newExpanded = new Set(expandedPrograms);
    if (newExpanded.has(programId)) {
      newExpanded.delete(programId);
    } else {
      newExpanded.add(programId);
    }
    setExpandedPrograms(newExpanded);
  };

  const getProgramsForOrg = (orgId: string) => {
    return programs.filter(p => p.organizationId === orgId);
  };

  const getGroupsForProgram = (programId: string) => {
    return groups.filter(g => g.programId === programId);
  };

  if (isLoading) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: '14px', color: '#666' }}>
          {t('common.loading')}...
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '16px', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      direction: isRTL ? 'rtl' : 'ltr'
    }}>
      <h3 style={{ 
        margin: '0 0 12px 0', 
        fontSize: '16px', 
        fontWeight: '600',
        color: '#333',
        textAlign: isRTL ? 'right' : 'left'
      }}>
        🗺️ {t('sitemap.title') || 'Geographic Distribution'}
      </h3>

      {/* Statistics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '8px',
        marginBottom: '12px'
      }}>
        <div style={{
          background: '#f8f9fa',
          padding: '8px',
          borderRadius: '6px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#333' }}>
            {organizations.length}
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {t('sitemap.organizations') || 'Orgs'}
          </div>
        </div>
        <div style={{
          background: '#f8f9fa',
          padding: '8px',
          borderRadius: '6px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#333' }}>
            {programs.length}
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {t('sitemap.programs') || 'Programs'}
          </div>
        </div>
        <div style={{
          background: '#f8f9fa',
          padding: '8px',
          borderRadius: '6px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#333' }}>
            {groups.length}
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {t('sitemap.groups') || 'Groups'}
          </div>
        </div>
      </div>

      {/* Hierarchy Tree - Scrollable */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto',
        paddingRight: isRTL ? '0' : '4px',
        paddingLeft: isRTL ? '4px' : '0'
      }}>
        {organizations.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            color: '#999',
            fontSize: '13px'
          }}>
            {t('sitemap.noData') || 'No data available'}
          </div>
        ) : (
          <div>
            {organizations.slice(0, 5).map((org) => {
              const orgPrograms = getProgramsForOrg(org.id);
              const isOrgExpanded = expandedOrgs.has(org.id);

              return (
                <div key={org.id} style={{ marginBottom: '8px' }}>
                  {/* Organization */}
                  <div
                    onClick={() => toggleOrganization(org.id)}
                    style={{
                      padding: '10px',
                      background: '#f0f4ff',
                      borderRadius: '6px',
                      border: '1px solid #e0e7ff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '13px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#e0e7ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f0f4ff';
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>
                      {isOrgExpanded ? '📂' : '📁'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {org.name}
                      </div>
                    </div>
                    <div style={{
                      background: '#e0e7ff',
                      color: '#4338ca',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '11px',
                      fontWeight: '500'
                    }}>
                      {orgPrograms.length}
                    </div>
                    <span style={{ 
                      fontSize: '14px', 
                      transform: isOrgExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                      display: 'inline-block'
                    }}>
                      {isRTL ? '◀' : '▶'}
                    </span>
                  </div>

                  {/* Programs under this Organization */}
                  {isOrgExpanded && (
                    <div style={{ 
                      [isRTL ? 'marginRight' : 'marginLeft']: '20px',
                      marginTop: '6px',
                      borderLeft: isRTL ? 'none' : '2px solid #e0e7ff',
                      borderRight: isRTL ? '2px solid #e0e7ff' : 'none',
                      [isRTL ? 'paddingRight' : 'paddingLeft']: '12px'
                    }}>
                      {orgPrograms.slice(0, 3).map((program) => {
                        const programGroups = getGroupsForProgram(program.id);
                        const isProgramExpanded = expandedPrograms.has(program.id);

                        return (
                          <div key={program.id} style={{ marginBottom: '6px' }}>
                            {/* Program */}
                            <div
                              onClick={() => toggleProgram(program.id)}
                              style={{
                                padding: '8px',
                                background: 'white',
                                borderRadius: '6px',
                                border: '1px solid #fee2e2',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '12px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#fef2f2';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                              }}
                            >
                              <span style={{ fontSize: '14px' }}>
                                {isProgramExpanded ? '📂' : '📁'}
                              </span>
                              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                <div style={{ fontWeight: '600', fontSize: '12px', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {program.name}
                                </div>
                              </div>
                              <div style={{
                                background: '#fee2e2',
                                color: '#991b1b',
                                padding: '2px 6px',
                                borderRadius: '8px',
                                fontSize: '10px',
                                fontWeight: '500'
                              }}>
                                {programGroups.length}
                              </div>
                              <span style={{ 
                                fontSize: '12px',
                                transform: isProgramExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                                display: 'inline-block'
                              }}>
                                {isRTL ? '◀' : '▶'}
                              </span>
                            </div>

                            {/* Groups under this Program */}
                            {isProgramExpanded && (
                              <div style={{ 
                                [isRTL ? 'marginRight' : 'marginLeft']: '20px',
                                marginTop: '4px',
                                borderLeft: isRTL ? 'none' : '2px solid #fee2e2',
                                borderRight: isRTL ? '2px solid #fee2e2' : 'none',
                                [isRTL ? 'paddingRight' : 'paddingLeft']: '10px'
                              }}>
                                {programGroups.slice(0, 2).map((group) => (
                                  <div
                                    key={group.id}
                                    style={{
                                      padding: '6px',
                                      background: 'white',
                                      borderRadius: '4px',
                                      border: '1px solid #e0f2fe',
                                      marginBottom: '4px',
                                      fontSize: '11px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    👥 {group.name}
                                  </div>
                                ))}
                                {programGroups.length > 2 && (
                                  <div style={{ fontSize: '10px', color: '#666', padding: '4px', textAlign: 'center' }}>
                                    +{programGroups.length - 2} more
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {orgPrograms.length > 3 && (
                        <div style={{ fontSize: '11px', color: '#666', padding: '4px', textAlign: 'center' }}>
                          +{orgPrograms.length - 3} more programs
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {organizations.length > 5 && (
              <div style={{ fontSize: '11px', color: '#666', padding: '8px', textAlign: 'center' }}>
                +{organizations.length - 5} more organizations
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// AI Assistant Widget - Fixed floating button
function AIAssistantWidget() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const isRTL = i18n.language === 'he';

  return (
    <>
      {/* Fixed floating button */}
      {!isOpen && (
        <button
          className="ai-chatbox-floating-button"
          onClick={() => setIsOpen(true)}
          aria-label={t('chat.openChat', 'Open AI Assistant')}
          title={t('chat.openChat', 'Open AI Assistant')}
          style={{
            position: 'fixed',
            bottom: '24px',
            ...(isRTL ? { left: '24px' } : { right: '24px' }),
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: '#000000',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
            fontSize: '28px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.4)';
          }}
        >
          🤖
        </button>
      )}

      {/* Floating chat window */}
      {isOpen && (
        <div className="ai-chatbox-floating-window">
          <AIChatBox onClose={() => setIsOpen(false)} />
        </div>
      )}
    </>
  );
}

// Widget Catalog
export const WIDGET_CATALOG = [
  {
    id: 'patients-overview',
    nameKey: 'widgets.patientsOverview',
    descriptionKey: 'widgets.desc.patientsOverview',
    icon: '👥',
    component: PatientsOverviewWidget
  },
  {
    id: 'total-patients',
    nameKey: 'widgets.totalPatients',
    descriptionKey: 'widgets.desc.totalPatients',
    icon: '👥',
    component: TotalPatientsWidget
  },
  {
    id: 'active-patients',
    nameKey: 'widgets.activePatients',
    descriptionKey: 'widgets.desc.activePatients',
    icon: '✅',
    component: ActivePatientsWidget
  },
  {
    id: 'inactive-cases',
    nameKey: 'widgets.inactiveCases',
    descriptionKey: 'widgets.desc.inactiveCases',
    icon: '📁',
    component: InactiveCasesWidget
  },
  {
    id: 'treatment-completion',
    nameKey: 'widgets.treatmentCompletion',
    descriptionKey: 'widgets.desc.treatmentCompletion',
    icon: '📊',
    component: TreatmentCompletionWidget
  },
  {
    id: 'recently-updated',
    nameKey: 'widgets.recentlyUpdated',
    descriptionKey: 'widgets.desc.recentlyUpdated',
    icon: '🔄',
    component: RecentlyUpdatedWidget
  },
  {
    id: 'total-cases',
    nameKey: 'widgets.totalCases',
    descriptionKey: 'widgets.desc.totalCases',
    icon: '📋',
    component: TotalCasesWidget
  },
  {
    id: 'events-overview',
    nameKey: 'widgets.eventsOverview',
    descriptionKey: 'widgets.desc.eventsOverview',
    icon: '📅',
    component: EventsOverviewWidget
  },
  {
    id: 'total-events',
    nameKey: 'widgets.totalEvents',
    descriptionKey: 'widgets.desc.totalEvents',
    icon: '📅',
    component: TotalEventsWidget
  },
  {
    id: 'upcoming-events',
    nameKey: 'widgets.upcomingEvents',
    descriptionKey: 'widgets.desc.upcomingEvents',
    icon: '⏰',
    component: UpcomingEventsWidget
  },
  {
    id: 'tasks-overview',
    nameKey: 'widgets.tasksOverview',
    descriptionKey: 'widgets.desc.tasksOverview',
    icon: '📋',
    component: TasksOverviewWidget
  },
  {
    id: 'total-tasks',
    nameKey: 'widgets.totalTasks',
    descriptionKey: 'widgets.desc.totalTasks',
    icon: '📋',
    component: TotalTasksWidget
  },
  {
    id: 'pending-tasks',
    nameKey: 'widgets.pendingTasks',
    descriptionKey: 'widgets.desc.pendingTasks',
    icon: '⏳',
    component: PendingTasksWidget
  },
  {
    id: 'completion-rate',
    nameKey: 'widgets.completionRate',
    descriptionKey: 'widgets.desc.completionRate',
    icon: '📊',
    component: CompletionRateWidget
  },
  {
    id: 'new-patients',
    nameKey: 'widgets.newPatients',
    descriptionKey: 'widgets.desc.newPatients',
    icon: '✨',
    component: NewPatientsWidget
  },
  {
    id: 'recent-activity',
    nameKey: 'widgets.recentActivity',
    descriptionKey: 'widgets.desc.recentActivity',
    icon: '📰',
    component: RecentActivityWidget,
    isWide: true
  },
  {
    id: 'today-tasks',
    nameKey: 'widgets.todayTasks',
    descriptionKey: 'widgets.desc.todayTasks',
    icon: '📋',
    component: TodayTasksWidget,
    isWide: true
  },
  {
    id: 'notifications-list',
    nameKey: 'widgets.notificationsList',
    descriptionKey: 'widgets.desc.notificationsList',
    icon: '🔔',
    component: NotificationsWidget,
    isWide: true
  },
  {
    id: 'today-milestones',
    nameKey: 'widgets.todayMilestones',
    descriptionKey: 'widgets.desc.todayMilestones',
    icon: '🎯',
    component: TodayMilestonesWidget,
    isWide: true
  },
  {
    id: 'inactive-patients-list',
    nameKey: 'widgets.inactivePatientsList',
    descriptionKey: 'widgets.desc.inactivePatientsList',
    icon: '⚠️',
    component: InactivePatientsWidget,
    isWide: true
  },
  {
    id: 'ai-assistant',
    nameKey: 'widgets.aiAssistant',
    descriptionKey: 'widgets.desc.aiAssistant',
    icon: '🤖',
    component: AIAssistantWidget
  },
  {
    id: 'geographic-sitemap',
    nameKey: 'widgets.geographicSitemap',
    descriptionKey: 'widgets.desc.geographicSitemap',
    icon: '🗺️',
    component: GeographicSitemapWidget,
    isWide: true,
    requiresPermission: 'canSeeSitemap'
  }
];
